import { configService } from '../services/configService';
import { setCachedProviderToken, getTokensFromProviderToken, getCachedAccessToken, getCachedIdentityToken } from '../services/identityService';
import { LogLevel, PublicClientApplication, EventType } from "@azure/msal-browser";
import { AuthProviderType, setAuthProvider } from './utilities';

const getMsAuthProviderType = () =>
    configService.msAuthIsGccHigh ? AuthProviderType.MicrosoftGovHigh : AuthProviderType.Microsoft;

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const getMsalConfig = () => {
    return {
        auth: {
            clientId: configService.msAuthClientId,
            authority: `https://login.microsoftonline.${configService.msAuthIsGccHigh ? 'us': 'com'}/common`,
            redirectUri: `${window.location.origin}`
        },
        cache: {
            cacheLocation: "sessionStorage", // This configures where your cache will be stored
            storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Info:
                            console.info(message);
                            return;
                        case LogLevel.Verbose:
                            console.debug(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            return;
                    }
                }
            },
            piiLoggingEnabled: false
        }
    };
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const msLoginRequest = {
    scopes: ["user.read"],
    prompt: 'select_account'
};

export let msAuth = null;
let loginCallback = null; // eslint-disable-line

export const initMsAuth = async (config) => {
    msAuth = new PublicClientApplication(getMsalConfig());
    await msAuth.initialize();
    msAuth.enableAccountStorageEvents();

    loginCallback = msAuth.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
            const account = event.payload.account;
            msAuth.setActiveAccount(account);
        }
    });

    return msAuth;
}

export const msLoginPopup = async () => {
    //set auth type
    setAuthProvider(getMsAuthProviderType());
    await msAuth.handleRedirectPromise();

    const loginResponse = await msAuth.loginPopup(msLoginRequest);

    return getTokensFromMicrosoft(loginResponse);
}

export const msLoginSSO = async () => {
    await msAuth.handleRedirectPromise();
    const account = msAuth.getActiveAccount();
    const msLoginRequest = {
        scopes: ["user.read"],
        account
    };

    return msAuth.ssoSilent(msLoginRequest);
}

export const msLogout = () => {
    msAuth.logoutRedirect();
}

export const getProviderTokenFromMicrosoft = (msToken) => {
    if (typeof msToken === 'object') {

        return {
            tenantId: msToken.tenantId,
            idToken: msToken.idToken,
            accessToken: msToken.accessToken,
            expiry: msToken.expiresOn
        };
    }

    return null;
}

const getTokensFromMicrosoft = async (msToken) => {
    let identityToken, accessToken;

    if (typeof msToken === 'object') {
        const providerToken = getProviderTokenFromMicrosoft(msToken);

        const cwTokens = await getTokensFromProviderToken(configService.appId, getMsAuthProviderType(), providerToken);
        identityToken = cwTokens.identityToken;
        accessToken = cwTokens.accessToken;

        setCachedProviderToken(providerToken);
    } else {
        identityToken = getCachedIdentityToken();
        accessToken = getCachedAccessToken();
    }

    return {
        identityToken,
        accessToken
    };
}

export const getTokensFromMicrosoftRedirect = async () => {
    return msAuth.handleRedirectPromise()
        .then(getTokensFromMicrosoft)
        .catch((err) => {
            throw (err);
        })
}

//export const getMsIdToken = (account) => {
//    const idTokenKey = `${account.homeAccountId}-${account.environment}-idtoken-${configService.msAuthClientId}-${account.tenantId}---`;
//    const idToken = localStorage.getItem(idTokenKey);

//    if (idToken) {
//        return JSON.parse(idToken)?.secret;
//    }

//    return null;
//}

//export const getMsAccessToken = (account) => {
//    const accessTokenKey = `${account.homeAccountId}-${account.environment}-accesstoken-${configService.msAuthClientId}-${account.tenantId}-calendars.read openid profile user.read email--`;
//    const accessToken = localStorage.getItem(accessTokenKey);

//    if (accessToken) {
//        return JSON.parse(accessToken)?.secret;
//    }

//    return null;
//}